import urlJoin from 'url-join';
import startsWith from 'lodash/fp/startsWith';

interface IEsRequestHeaders {
    readonly companyAlias: string;
    readonly facilityAlias: string;
}

type InjectedSettings = typeof injectedAppSettings;

interface IAppSettings {
    /**
     * Base URL for the ES2 WebApi.
     */
    readonly apiUrl: string;

    /**
     * Base URL for the Auth WebApp.
     */
    readonly authUrl: string;

    /**
     * Base URL for the Cumulus WebApi.
     */
    readonly cumulusApiUrl: string;

    /**
     * Base URL for the ES2 public API.
     */
    readonly publicApiUrl: string;

    /**
     * Base URL for GraphQL
     */
    readonly graphQLUrl: string;

    /**
     * URL to the reporting server.
     */
    readonly reportsServerUrl: string;

    /**
     * API key used for Google Maps.
     */
    readonly googleMapsApiKey: string;

    /**
     * API key used for Mapbox
     */
    readonly mapboxApiKey: string;

    /**
     * The build/release number for this version.
     */
    readonly releaseNumber: string;

    readonly isDevelopment: boolean;
    readonly environment: string;

    /**
     * Request headers required for authorization
     */
    readonly requestHeaders: IEsRequestHeaders;

    buildApiUrl(...segments: string[]): string;
    buildPublicApiUrl(...segments: string[]): string;
}

let injectedAppSettings = {
    get omnisUrl(): string {
        return '#{OmnisHostUri}';
    },
    get apiUrl(): string {
        return '#{WebApiAppHostUri}';
    },
    get authUrl(): string {
        return '#{appSettings:LoginUri}';
    },
    get graphQLUrl(): string {
        return '#{GraphQLApiUri}';
    },
    get cumulusApiUrl(): string {
        return '#{CumulusAppHostUri}';
    },
    get reportsServerUrl(): string {
        return '#{ReportsAppHostUri}';
    },
    get publicApiUrl(): string {
        return '#{ES2VersionedApiUri}';
    },
    get environment(): string {
        return '#{Environment}';
    },
    get googleMapsApiKey(): string {
        return '#{GoogleMapsApiKey.DomainRestricted}';
    },
    get mapboxApiKey(): string {
        return '#{MapboxApiKey.DomainRestricted}';
    },
    get releaseNumber(): string {
        return '#{Octopus.Release.Number}';
    },
    get googleAnalyticsMeasurementId(): string {
        return '#{GoogleAnalyticsMeasurementId}';
    },
    get languagesExplicitlyAllowed(): string {
        return '#{LanguagesExplicitlyAllowed}';
    },
    get apiKey(): string {
        return '';
    },
};

// Cater for development where Octopus doesn't run, hence no substitution
if (startsWith('#{', injectedAppSettings.cumulusApiUrl)) {
    const PUBLIC_API_HOST = import.meta.env.VITE_PUBLIC_API_HOST || `http://localhost:60000/api`;
    const API_HOST = import.meta.env.VITE_API_HOST || `http://localhost:61000/api`;
    const CUMULUS_API_HOST = import.meta.env.VITE_CUMULUS_API_HOST || `https://localhost:7123`;
    const AUTH_HOST = import.meta.env.VITE_AUTH_HOST || `http://localhost:62000`;
    const REPORTING_HOST = import.meta.env.VITE_REPORING_HOST || `https://localhost:5001`;
    const GRAPHQL_HOST = import.meta.env.VITE_GRAPHQL_HOST || `http://localhost:60000/api/graphql`;
    const OMNIS_HOST = import.meta.env.VITE_OMNIS_HOST || `http://localhost:3000`;

    // Check if we have stuff in environment variables
    injectedAppSettings = {
        get omnisUrl(): string {
            return OMNIS_HOST;
        },
        get apiUrl(): string {
            return API_HOST;
        },
        get authUrl(): string {
            return AUTH_HOST;
        },
        get graphQLUrl(): string {
            return GRAPHQL_HOST;
        },
        get cumulusApiUrl(): string {
            return CUMULUS_API_HOST;
        },
        get reportsServerUrl(): string {
            return REPORTING_HOST;
        },
        get publicApiUrl(): string {
            return PUBLIC_API_HOST;
        },
        get environment(): string {
            return 'Development';
        },
        get googleMapsApiKey(): string {
            return 'AIzaSyAZsoqQGsDFID-waQbSinVR3NQkq9j-msQ';
        },
        get mapboxApiKey(): string {
            return (
                import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ||
                'pk.eyJ1IjoiZW52aXJvc3VpdGUtYnJpc2JhbmUiLCJhIjoiY2sybzY1OWNpMDd6ZDNkczd4Z2Z2cWxxZyJ9.-wuqJp_YE_zFr4JjPsZHAg'
            );
        },
        get releaseNumber(): string {
            return 'Development';
        },
        get googleAnalyticsMeasurementId(): string {
            return 'UA-8575676-12';
        },
        get languagesExplicitlyAllowed(): string {
            return '';
        },
        get apiKey(): string {
            return import.meta.env.VITE_API_KEY ?? '';
        },
    };
} else if (!startsWith('http', injectedAppSettings.cumulusApiUrl)) {
    throw new Error(
        `Cumulus API URL is not set correctly. Expected a URL starting with 'http(s)' but got '${injectedAppSettings.cumulusApiUrl}'. Please check your environment variables or configuration.`
    );
}

const requestHeaders: IEsRequestHeaders = {
    companyAlias: 'PEL-Company-Alias',
    facilityAlias: 'PEL-Facility-Alias',
};

const createUrlBuilder =
    (urlBase: string) =>
    (...segments: string[]) =>
        urlJoin(urlBase, ...segments);

const AppSettings: IAppSettings & InjectedSettings = {
    ...injectedAppSettings,

    get isDevelopment(): boolean {
        return import.meta.env.MODE === 'development';
    },
    get requestHeaders(): IEsRequestHeaders {
        return requestHeaders;
    },

    buildApiUrl: createUrlBuilder(injectedAppSettings.apiUrl),
    buildPublicApiUrl: createUrlBuilder(injectedAppSettings.publicApiUrl),
};

export default AppSettings;
